import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Search,
  ChevronRight,
  Sparkles,
  ChevronLeft,
  Trash2,
  Check,
  X,
  BookOpen,
  Info,
  Edit2,
  Layers,
  Zap,
  HelpCircle,
  RefreshCcw
} from "lucide-react";
import apiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const TAB_ORDER = ["questions", "recommendations"];

const KnowledgeBase = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("questions");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const QUESTIONS_PER_PAGE = 10;

  // New Question / Generate
  const [newQuestion, setNewQuestion] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [recommendedVariations, setRecommendedVariations] = useState([]);

  // Manual Response Form State
  const [showManualResForm, setShowManualResForm] = useState(null);
  const [manualInstruction, setManualInstruction] = useState("");
  const [manualTrigger, setManualTrigger] = useState("");

  // Editing State
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionText, setEditingQuestionText] = useState("");

  const [editingResponseId, setEditingResponseId] = useState(null);
  const [editingInstruction, setEditingInstruction] = useState("");
  const [editingTrigger, setEditingTrigger] = useState("");

  // New Topic Modal
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [topicName, setTopicName] = useState("");
  const [topicDesc, setTopicDesc] = useState("");

  const [expandedQuestions, setExpandedQuestions] = useState([]);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState({
    id: null,
    type: null,
    extraId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTopics = React.useCallback(async () => {
    try {
      const data = await apiClient.getKBTopics();
      setTopics(data);
    } catch (err) {
      console.error("Failed to fetch topics", err);
    }
  }, []);

  const fetchQuestions = async (topicId) => {
    setLoading(true);
    try {
      const data = await apiClient.getKBQuestions(topicId);
      setQuestions(data);
    } catch (err) {
      console.error("Failed to fetch questions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
    setSelectedTopic(null);
  }, [activeAiAgentId, fetchTopics]);

  useEffect(() => {
    if (topics.length > 0 && !selectedTopic) {
      setSelectedTopic(topics[0]);
    }
  }, [topics, selectedTopic]);

  useEffect(() => {
    if (selectedTopic) {
      fetchQuestions(selectedTopic.id);
      setCurrentPage(1);
      setSearchQuery("");
    }
  }, [selectedTopic]);

  const handleCreateTopic = async () => {
    if (!topicName.trim()) return;
    try {
      await apiClient.createKBTopic(topicName, topicDesc);
      setTopicName("");
      setTopicDesc("");
      setShowTopicModal(false);
      fetchTopics();
      showToast("Knowledge domain created successfully", "success");
    } catch (err) {
      console.error("Failed to create topic", err);
      showToast("Failed to create domain", "error");
    }
  };

  const handleDeleteTopic = (e, topicId) => {
    e.stopPropagation();
    setDeleteData({ id: topicId, type: "topic" });
    setIsDeleteModalOpen(true);
  };

  const handleGenerateVariations = async () => {
    if (!newQuestion.trim() || !selectedTopic) {
      if (!selectedTopic) {
        showToast("Please select a knowledge domain first", "warning");
      }
      return;
    }
    setIsGenerating(true);
    setActiveTab("recommendations");
    try {
      const variations = await apiClient.generateFAQVariations(
        selectedTopic.name,
        newQuestion,
      );
      setRecommendedVariations(
        variations.map((qText) => ({
          question_text: qText,
          instruction_text: "",
          trigger_condition: "",
        })),
      );
    } catch (err) {
      console.error("Failed to generate variations", err);
      showToast("Failed to generate variations", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateVariationInput = (index, field, value) => {
    setRecommendedVariations((prev) => {
      const newArr = [...prev];
      newArr[index] = { ...newArr[index], [field]: value };
      return newArr;
    });
  };

  const handleApproveVariation = async (variation, index) => {
    if (
      !variation.instruction_text.trim() ||
      !variation.trigger_condition.trim()
    ) {
      showToast(
        "Please provide both Instruction and Trigger Condition before approving.",
        "warning",
      );
      return;
    }

    try {
      const qRes = await apiClient.createKBQuestion(
        selectedTopic.id,
        variation.question_text,
      );
      const questionId = qRes.id;

      await apiClient.createKBResponse(
        questionId,
        variation.instruction_text,
        variation.trigger_condition,
      );

      setRecommendedVariations((prev) => prev.filter((_, i) => i !== index));
      fetchQuestions(selectedTopic.id);
      showToast("Variation approved and added to knowledge base.", "success");
    } catch (err) {
      console.error("Failed to approve variation", err);
      showToast("Failed to approve variation", "error");
    }
  };

  const handleAddManualResponse = async (questionId) => {
    if (!manualInstruction.trim() || !manualTrigger.trim()) {
      showToast("Please fill in both fields.", "warning");
      return;
    }

    try {
      await apiClient.createKBResponse(
        questionId,
        manualInstruction,
        manualTrigger,
      );
      setManualInstruction("");
      setManualTrigger("");
      setShowManualResForm(null);
      fetchQuestions(selectedTopic.id);
      showToast("Manual variation committed successfully.", "success");
    } catch (err) {
      console.error("Failed to add manual response", err);
      showToast("Failed to add response variation", "error");
    }
  };

  const handleDeleteQuestion = (questionId) => {
    setDeleteData({ id: questionId, type: "question" });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteResponse = (responseId, questionId) => {
    setDeleteData({ id: responseId, type: "response", extraId: questionId });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (deleteData.type === "topic") {
        await apiClient.deleteKBTopic(deleteData.id);
        if (selectedTopic?.id === deleteData.id) setSelectedTopic(null);
        fetchTopics();
        showToast("Knowledge domain deleted successfully.", "success");
      } else if (deleteData.type === "question") {
        await apiClient.deleteKBQuestion(deleteData.id);
        fetchQuestions(selectedTopic.id);
        showToast("Question deleted.", "success");
      } else if (deleteData.type === "response") {
        await apiClient.deleteKBResponse(deleteData.id);
        fetchQuestions(selectedTopic.id);
        showToast("Response variation deleted.", "success");
      }
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error(`Failed to delete ${deleteData.type}`, err);
      showToast("Failed to delete item", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateQuestion = async (questionId) => {
    if (!editingQuestionText.trim()) return;
    try {
      await apiClient.updateKBQuestion(questionId, editingQuestionText);
      setEditingQuestionId(null);
      fetchQuestions(selectedTopic.id);
      showToast("Question updated.", "success");
    } catch (err) {
      console.error("Failed to update question", err);
      showToast("Failed to update question", "error");
    }
  };

  const handleUpdateResponse = async (responseId) => {
    if (!editingInstruction.trim() || !editingTrigger.trim()) return;
    try {
      await apiClient.updateKBResponse(
        responseId,
        editingInstruction,
        editingTrigger,
      );
      setEditingResponseId(null);
      fetchQuestions(selectedTopic.id);
      showToast("Variation logic updated.", "success");
    } catch (err) {
      console.error("Failed to update response", err);
      showToast("Failed to update variation", "error");
    }
  };

  const toggleQuestionExpansion = (id) => {
    setExpandedQuestions((prev) =>
      prev.includes(id) ? prev.filter((qId) => qId !== id) : [...prev, id],
    );
  };

  // Filtered and paginated questions
  const filteredQuestions = questions.filter((q) =>
    searchQuery.trim() === ""
      ? true
      : (q.text || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredQuestions.length / QUESTIONS_PER_PAGE);
  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE,
  );

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      {/* Full width container without max-w-7xl constraint */}
      <div className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header Section matching DocumentUpload & KnowledgeGraph */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Smart FAQ Builder
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Train your AI assistant with context-aware question and answer variations to handle customer queries with high precision.
            </p>
          </div>

          <div className="self-start sm:self-auto flex flex-col xl:flex-row items-start xl:items-center gap-4">
            {/* Minimal Tab Switcher */}
            <div className="flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
              <button
                onClick={() => setActiveTab("questions")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "questions"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <HelpCircle size={13} />
                <span>FAQ {questions.length > 0 ? `(${questions.length})` : ""}</span>
              </button>
              <button
                onClick={() => setActiveTab("recommendations")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "recommendations"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Sparkles size={13} />
                <span>Idea Lab</span>
              </button>
            </div>

            {/* Build New Topic Button */}
            <button
              onClick={() => setShowTopicModal(true)}
              className="flex items-center gap-2 px-5 py-2 sm:py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-zinc-800 transition-all shadow-xs active:scale-95 whitespace-nowrap"
            >
              <Plus size={14} />
              <span>New Topic</span>
            </button>
          </div>
        </div>

        {/* Top-Level Sliding Tab Track matching KnowledgeGraph */}
        <div className="overflow-hidden w-full pt-1">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 2}%)`,
              width: "200%",
            }}
          >
            {/* Panel 1: FAQ Questions & Domains */}
            <div className="w-1/2 shrink-0 px-2 sm:px-3">
              <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-stretch min-h-[calc(100vh-185px)]">
                {/* Left Column: Topics Sidebar */}
                <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs p-5 flex flex-col min-h-[calc(100vh-185px)] space-y-4">
                  <div className="pb-3 border-b border-zinc-50 shrink-0">
                    <h3 className="text-xs font-medium text-zinc-400">
                      Topics
                    </h3>
                    <p className="text-xs font-bold text-zinc-900 tracking-tight">
                      Knowledge Domains
                    </p>
                  </div>

                  {/* Topics List */}
                  <div className="flex-1 flex flex-col space-y-1.5 overflow-y-auto pr-1 custom-scrollbar min-h-0">
                    {topics.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-zinc-50/50 rounded-2xl border border-dashed border-zinc-200">
                        <HelpCircle
                          size={28}
                          className="mx-auto text-zinc-300 mb-2 opacity-40"
                        />
                        <p className="text-xs font-medium text-zinc-400">
                          No Topics Yet
                        </p>
                        <button
                          onClick={() => setShowTopicModal(true)}
                          className="mt-3 px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-black transition-all shadow-sm flex items-center gap-1.5"
                        >
                          <Plus size={12} />
                          <span>Create Topic</span>
                        </button>
                      </div>
                    ) : (
                      topics.map((topic) => (
                        <div
                          key={topic.id}
                          onClick={() => setSelectedTopic(topic)}
                          className={`group p-2.5 px-3.5 rounded-xl cursor-pointer transition-all flex justify-between items-center border ${
                            selectedTopic?.id === topic.id
                              ? "bg-zinc-900 text-white border-zinc-900 shadow-sm"
                              : "bg-zinc-50/50 border-zinc-100 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-200"
                          }`}
                        >
                          <div className="flex items-center gap-2.5 overflow-hidden min-w-0">
                            <div
                              className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                                selectedTopic?.id === topic.id
                                  ? "bg-zinc-800 text-white"
                                  : "bg-white text-zinc-400 shadow-sm"
                              }`}
                            >
                              <Layers size={14} />
                            </div>
                            <span className="text-xs font-bold truncate tracking-tight">
                              {topic.name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => handleDeleteTopic(e, topic.id)}
                            className={`p-1.5 rounded-lg border transition-all shrink-0 ${
                              selectedTopic?.id === topic.id
                                ? "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                                : "bg-white border-zinc-100 text-zinc-400 hover:text-red-500 hover:bg-red-50"
                            }`}
                            title="Delete Topic"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Right Column: Active Domain Questions Content */}
                <div className="flex flex-col min-w-0 min-h-[calc(100vh-185px)] space-y-6">
                  {selectedTopic ? (
                    <>
                      {/* Topic Header Card */}
                      <div className="bg-white rounded-2xl p-6 border border-zinc-100 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5 min-w-0">
                          <Layers className="text-zinc-700 shrink-0" size={18} />
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-zinc-900 truncate">
                              {selectedTopic.name}
                            </h3>
                            <p className="text-zinc-400 text-[11px] font-medium leading-relaxed mt-0.5 truncate">
                              {selectedTopic.description ||
                                `Knowledge repository for ${selectedTopic.name}.`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="bg-zinc-100 text-zinc-600 px-3 py-1.5 rounded-xl text-[9px] font-semibold tracking-wide">
                            {questions.length} FAQ Items
                          </span>
                        </div>
                      </div>

                      {/* Search Bar */}
                      <div className="relative">
                        <Search
                          size={14}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
                        />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                          }}
                          placeholder="Search questions in this topic..."
                          className="w-full pl-9 pr-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-xs font-medium focus:ring-1 focus:ring-zinc-900 focus:bg-white transition-all outline-none"
                        />
                      </div>

                      {/* Question List Card */}
                      <div className="bg-white rounded-2xl p-6 border border-zinc-100 shadow-sm space-y-3">
                        <div className="flex items-center justify-between mb-4 px-1">
                          <h3 className="text-sm font-semibold text-zinc-900">
                            Question Variations Repository
                          </h3>
                          <span className="bg-zinc-100 text-zinc-400 px-3 py-1 rounded-md text-[8px] font-semibold tracking-wide">
                            {filteredQuestions.length} Questions Total
                          </span>
                        </div>

                        {loading ? (
                          <div className="flex items-center justify-center py-12 text-zinc-400">
                            <RefreshCcw size={20} className="animate-spin" />
                          </div>
                        ) : filteredQuestions.length === 0 ? (
                          <div className="text-center py-12">
                            <BookOpen size={32} className="text-zinc-200 mx-auto mb-3" />
                            <p className="text-xs font-medium text-zinc-400">
                              {questions.length === 0 ? "Knowledge Base Empty" : "No Matching Questions"}
                            </p>
                            <p className="text-[9px] text-zinc-400 mt-1">
                              {questions.length === 0
                                ? "This topic has no questions yet. Use the Idea Lab tab above to brainstorm or add manual variations."
                                : "Try a different search query."}
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-3">
                              {paginatedQuestions.map((q) => (
                                <div
                                  key={q.id}
                                  className="bg-zinc-50/40 border border-zinc-100 rounded-xl overflow-hidden hover:border-zinc-200 transition-all duration-200"
                                >
                                  <div
                                    onClick={() => toggleQuestionExpansion(q.id)}
                                    className="px-4 py-3 flex items-center justify-between cursor-pointer group/item"
                                  >
                                    <div className="flex items-center gap-3.5 flex-1 min-w-0 mr-4">
                                      <div
                                        className={`p-1.5 rounded-lg transition-all shrink-0 ${
                                          expandedQuestions.includes(q.id)
                                            ? "bg-zinc-900 text-white rotate-90"
                                            : "bg-white text-zinc-400 group-hover/item:bg-zinc-100 shadow-sm"
                                        }`}
                                      >
                                        <ChevronRight size={14} />
                                      </div>

                                      {editingQuestionId === q.id ? (
                                        <div className="flex items-start gap-2.5 flex-1">
                                          <textarea
                                            value={editingQuestionText}
                                            onChange={(e) =>
                                              setEditingQuestionText(e.target.value)
                                            }
                                            autoFocus
                                            className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-xs leading-relaxed resize-none h-16 transition-all"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div className="flex flex-col gap-1.5 pt-0.5">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleUpdateQuestion(q.id);
                                              }}
                                              className="p-1.5 bg-zinc-900 text-white rounded-lg hover:bg-black transition-all"
                                            >
                                              <Check size={14} />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingQuestionId(null);
                                              }}
                                              className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all"
                                            >
                                              <X size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex flex-col min-w-0">
                                          <span className="text-xs font-bold text-zinc-900 leading-tight mb-1 truncate">
                                            {q.text}
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white text-zinc-500 border border-zinc-200/60 shadow-sm">
                                              {q.responses?.length || 0} variations
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex gap-1.5 opacity-0 group-hover/item:opacity-100 transition-opacity shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingQuestionId(q.id);
                                          setEditingQuestionText(q.text);
                                          if (!expandedQuestions.includes(q.id))
                                            toggleQuestionExpansion(q.id);
                                        }}
                                        className="p-1.5 text-zinc-400 bg-white border border-zinc-100 hover:text-zinc-900 hover:bg-zinc-50 rounded-lg transition-all shadow-sm"
                                        title="Edit Question"
                                      >
                                        <Edit2 size={13} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuestion(q.id);
                                        }}
                                        className="p-1.5 text-zinc-400 bg-white border border-zinc-100 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shadow-sm"
                                        title="Delete Question"
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Expanded Responses List */}
                                  {expandedQuestions.includes(q.id) && (
                                    <div className="px-5 pb-5 pt-2 bg-zinc-50/50 border-t border-zinc-100 animate-fadeIn ml-8 mr-3 mb-3 rounded-xl space-y-3">
                                      {q.responses?.map((resp) => (
                                        <div
                                          key={resp.id}
                                          className="bg-white border border-zinc-100 rounded-xl p-4 shadow-sm relative space-y-2.5"
                                        >
                                          <div className="flex justify-between items-start gap-4">
                                            <div className="flex-1 min-w-0">
                                              <div className="text-xs font-medium text-zinc-600 mb-1.5 flex items-center gap-1.5">
                                                <Info size={11} className="text-zinc-400" />
                                                Variation Logic
                                              </div>
                                              {editingResponseId === resp.id ? (
                                                <div className="space-y-3">
                                                  <textarea
                                                    value={editingInstruction}
                                                    onChange={(e) =>
                                                      setEditingInstruction(e.target.value)
                                                    }
                                                    className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-xs leading-relaxed h-20 outline-none"
                                                  />
                                                  <div className="space-y-1">
                                                    <label className="text-xs font-medium text-zinc-600">
                                                      Trigger Condition
                                                    </label>
                                                    <input
                                                      value={editingTrigger}
                                                      onChange={(e) =>
                                                        setEditingTrigger(e.target.value)
                                                      }
                                                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-xs outline-none"
                                                    />
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="space-y-2">
                                                  <div className="bg-zinc-50/70 border border-zinc-100 p-3 rounded-lg">
                                                    <p className="text-zinc-900 font-medium text-[11px] leading-relaxed">
                                                      "{resp.instruction_text}"
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-md">
                                                      Trigger
                                                    </span>
                                                    <p className="text-[11px] font-medium text-zinc-600 truncate">
                                                      {resp.trigger_condition}
                                                    </p>
                                                  </div>
                                                </div>
                                              )}
                                            </div>

                                            <div className="flex gap-1.5 shrink-0">
                                              {editingResponseId === resp.id ? (
                                                <>
                                                  <button
                                                    onClick={() => handleUpdateResponse(resp.id)}
                                                    className="p-1.5 bg-zinc-900 text-white rounded-lg hover:bg-black transition-all flex items-center justify-center"
                                                  >
                                                    <Check size={13} />
                                                  </button>
                                                  <button
                                                    onClick={() => setEditingResponseId(null)}
                                                    className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all flex items-center justify-center"
                                                  >
                                                    <X size={13} />
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      setEditingResponseId(resp.id);
                                                      setEditingInstruction(resp.instruction_text);
                                                      setEditingTrigger(resp.trigger_condition);
                                                    }}
                                                    className="p-1.5 text-zinc-400 bg-white border border-zinc-100 hover:text-zinc-900 hover:bg-zinc-50 rounded-lg transition-all shadow-sm"
                                                    title="Edit Response"
                                                  >
                                                    <Edit2 size={13} />
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteResponse(resp.id, q.id)}
                                                    className="p-1.5 text-zinc-400 bg-white border border-zinc-100 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shadow-sm"
                                                    title="Delete Response"
                                                  >
                                                    <Trash2 size={13} />
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}

                                      {/* Manual Form / Add Variation */}
                                      {showManualResForm === q.id ? (
                                        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-lg animate-scaleIn mt-3 space-y-3">
                                          <div className="flex items-center gap-2">
                                            <Plus className="text-zinc-700 shrink-0" size={18} />
                                            <h4 className="text-sm font-semibold text-zinc-900">
                                              Add New Variation
                                            </h4>
                                          </div>
                                          <div className="space-y-3">
                                            <div className="space-y-1">
                                              <label className="text-xs font-medium text-zinc-600">
                                                Instruction
                                              </label>
                                              <textarea
                                                value={manualInstruction}
                                                onChange={(e) => setManualInstruction(e.target.value)}
                                                placeholder="Enter specific instruction..."
                                                className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 font-medium text-xs h-20 resize-none outline-none"
                                              />
                                            </div>
                                            <div className="space-y-1">
                                              <label className="text-xs font-medium text-zinc-600">
                                                Trigger Condition
                                              </label>
                                              <input
                                                value={manualTrigger}
                                                onChange={(e) => setManualTrigger(e.target.value)}
                                                placeholder="e.g. When customer asks about status"
                                                className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-xs outline-none"
                                              />
                                            </div>
                                            <div className="flex gap-2 pt-1">
                                              <button
                                                onClick={() => setShowManualResForm(null)}
                                                className="flex-1 px-3 py-2 text-zinc-400 hover:text-zinc-900 font-bold text-xs rounded-xl"
                                              >
                                                Discard
                                              </button>
                                              <button
                                                onClick={() => handleAddManualResponse(q.id)}
                                                className="flex-1 px-3 py-2 bg-zinc-900 text-white font-bold text-xs rounded-xl hover:bg-zinc-800 transition-all shadow-sm"
                                              >
                                                Save Variation
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => setShowManualResForm(q.id)}
                                          className="w-full py-2.5 rounded-xl border border-dashed border-zinc-200 text-zinc-600 font-medium text-xs hover:border-zinc-900 hover:text-zinc-900 transition-all bg-white shadow-2xs"
                                        >
                                          + Add Manual Variation
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between pt-4 border-t border-zinc-50">
                                <span className="text-xs font-medium text-zinc-600 px-2">
                                  Page {currentPage} of {totalPages}
                                </span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg border border-zinc-100 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 transition-all"
                                  >
                                    <ChevronLeft size={14} />
                                  </button>
                                  <button
                                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg border border-zinc-100 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 transition-all"
                                  >
                                    <ChevronRight size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-2xl p-12 border border-zinc-100 shadow-xs text-center flex flex-col items-center justify-center flex-1 h-full min-h-[calc(100vh-185px)] space-y-4">
                      <HelpCircle size={40} className="text-zinc-300 mx-auto opacity-40 mb-1" />
                      <h3 className="text-sm font-semibold text-zinc-900">
                        {topics.length === 0 ? "No Knowledge Domains" : "Select a Knowledge Domain"}
                      </h3>
                      <p className="text-zinc-400 text-xs font-medium max-w-md leading-relaxed">
                        {topics.length === 0
                          ? "Build a knowledge domain from the left panel to begin adding context-aware FAQ variations."
                          : "Choose a domain from the left sidebar to inspect and manage question variations."}
                      </p>
                      {topics.length === 0 && (
                        <button
                          onClick={() => setShowTopicModal(true)}
                          className="mt-2 px-7 py-3 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-black transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                        >
                          <Plus size={14} />
                          <span>Create Domain</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Panel 2: Recommendations / AI Idea Lab Workspace */}
            <div className="w-1/2 shrink-0 px-2 sm:px-3 border-l border-zinc-200/50 space-y-6 min-h-[calc(100vh-185px)] flex flex-col">
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-100 shadow-xs relative overflow-hidden space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="text-zinc-700 shrink-0" size={18} />
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900">
                        Idea Lab
                      </h3>
                      <p className="text-zinc-400 font-medium text-[11px]">
                        Brainstorm context-aware questions from your data
                      </p>
                    </div>
                  </div>

                  {/* Target Domain Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400 shrink-0">
                      Target Domain:
                    </span>
                    {topics.length === 0 ? (
                      <span className="text-xs text-amber-600 font-bold">No domains created yet</span>
                    ) : (
                      <select
                        value={selectedTopic?.id || ""}
                        onChange={(e) => {
                          const t = topics.find((top) => top.id === parseInt(e.target.value));
                          if (t) setSelectedTopic(t);
                        }}
                        className="px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-bold text-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none"
                      >
                        {topics.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <p className="text-zinc-500 font-medium text-[11px] leading-relaxed">
                  Enter a seed question or customer scenario. The AI will synthesize 3 distinct variations a user might ask, then craft corresponding logic and trigger conditions.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search
                      size={14}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
                    />
                    <input
                      type="text"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      placeholder="e.g. Order Tracking or Return Policy"
                      className="w-full pl-9 pr-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-xs font-medium focus:ring-1 focus:ring-zinc-900 focus:bg-white transition-all outline-none"
                    />
                  </div>
                  <button
                    onClick={handleGenerateVariations}
                    disabled={isGenerating || !newQuestion.trim() || !selectedTopic}
                    className="px-6 py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 shadow-md disabled:opacity-50 active:scale-95 shrink-0"
                  >
                    {isGenerating ? (
                      <RefreshCcw size={13} className="animate-spin" />
                    ) : (
                      <Zap size={13} />
                    )}
                    <span>{isGenerating ? "Brainstorming..." : "Generate"}</span>
                  </button>
                </div>
              </div>

              {/* Brainstorm Results */}
              {recommendedVariations.length > 0 && (
                <div className="space-y-4 animate-slideUp">
                  <div className="flex items-center gap-2 px-1">
                    <div className="w-1 h-4 bg-zinc-900 rounded-full"></div>
                    <h4 className="text-xs font-semibold text-zinc-900">
                      Generated Variations ({recommendedVariations.length})
                    </h4>
                  </div>

                  <div className="space-y-4">
                    {recommendedVariations.map((v, i) => (
                      <div
                        key={i}
                        className="bg-white border border-zinc-100 rounded-2xl p-5 shadow-xs space-y-4"
                      >
                        <div>
                          <label className="text-xs font-medium text-zinc-600 mb-1.5 block">
                            Question Variation
                          </label>
                          <input
                            type="text"
                            value={v.question_text}
                            onChange={(e) =>
                              handleUpdateVariationInput(i, "question_text", e.target.value)
                            }
                            className="w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-xl text-xs font-bold text-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none"
                          />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-xs font-medium text-zinc-600 block">
                              Variation Logic
                            </label>
                            <textarea
                              value={v.instruction_text}
                              onChange={(e) =>
                                handleUpdateVariationInput(
                                  i,
                                  "instruction_text",
                                  e.target.value,
                                )
                              }
                              placeholder="How should the AI respond?"
                              className="w-full p-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 font-medium text-xs h-20 focus:ring-1 focus:ring-zinc-900 outline-none resize-none"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-xs font-medium text-zinc-600 block">
                              Trigger Condition
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={v.trigger_condition}
                                onChange={(e) =>
                                  handleUpdateVariationInput(
                                    i,
                                    "trigger_condition",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g. When specific order status is requested"
                                className="w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 font-medium text-xs focus:ring-1 focus:ring-zinc-900 outline-none"
                              />
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                                <Zap size={13} />
                              </div>
                            </div>
                            <div className="p-2.5 bg-zinc-50 rounded-xl border border-zinc-100 flex items-start gap-2">
                              <Info size={12} className="text-zinc-400 mt-0.5 shrink-0" />
                              <p className="text-[10px] font-medium text-zinc-500 leading-normal">
                                Triggers ensure this variation is prioritized under relevant queries.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex bg-zinc-50/50 border-t border-zinc-100 pt-3 gap-2">
                          <button
                            onClick={() => handleApproveVariation(v, i)}
                            className="flex-1 py-2 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-black transition-all flex items-center justify-center gap-1.5 shadow-sm"
                          >
                            <Check size={14} />
                            Approve & Train
                          </button>
                          <button
                            onClick={() =>
                              setRecommendedVariations((prev) =>
                                prev.filter((_, idx) => idx !== i),
                              )
                            }
                            className="flex-1 py-2 bg-white text-red-500 border border-zinc-100 hover:bg-red-50 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 shadow-sm"
                          >
                            <Trash2 size={14} />
                            Discard
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isGenerating && (
                <div className="flex flex-col items-center justify-center py-12 animate-pulse">
                  <div className="w-8 h-8 border-3 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mb-3"></div>
                  <p className="text-zinc-400 font-semibold tracking-wide text-[10px]">
                    Generating Variations...
                  </p>
                </div>
              )}


            </div>
          </div>
        </div>
      </div>

      {/* New Topic Modal */}
      {showTopicModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-['Inter',sans-serif]">
            <div 
              className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm animate-fadeIn"
              onClick={() => setShowTopicModal(false)}
            />
            <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl shadow-zinc-900/20 animate-slideIn border border-zinc-200/90 overflow-hidden z-10">
              <button
                onClick={() => setShowTopicModal(false)}
                className="absolute top-4 right-4 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                aria-label="Close dialog"
              >
                <X size={16} />
              </button>

              <div className="p-5 sm:p-6 pb-2 pr-12">
                <h3 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight">
                  New Knowledge Domain
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                  Create a domain category for questions & answers
                </p>
              </div>

              <div className="p-5 sm:p-6 pt-2 space-y-3.5">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-zinc-700 block">
                    Domain Name
                  </label>
                  <input
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    placeholder="e.g. Refund Policy or Product FAQ"
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-medium text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-zinc-700 block">
                    Objective (Optional)
                  </label>
                  <textarea
                    value={topicDesc}
                    onChange={(e) => setTopicDesc(e.target.value)}
                    placeholder="Describe the scope for this knowledge domain..."
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 leading-relaxed h-24 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 resize-none transition-all"
                  />
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => setShowTopicModal(false)}
                    className="px-3.5 py-2 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateTopic}
                    disabled={!topicName.trim()}
                    className="px-4 py-2 bg-zinc-900 text-white rounded-lg font-medium text-xs hover:bg-zinc-800 active:bg-black transition-colors shadow-xs disabled:opacity-40"
                  >
                    Create Domain
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title={`Delete ${
          deleteData.type === "topic"
            ? "Knowledge Domain"
            : deleteData.type === "question"
            ? "Question"
            : "Variation Response"
        }?`}
        message={`This action will permanently delete this ${deleteData.type} from the knowledge base.`}
      />
    </div>
  );
};

export default KnowledgeBase;
